<script setup lang="ts">
// Header of the user manual (page and help dialog): title and search. Texts come in as
// props, so the parent's useUserManual() translation stays the single source.
defineProps<{
    /** Prefix of the element ids Cypress relies on: user-manual-page or user-manual-dialog. */
    idPrefix: string;
    heading: string;
    placeholder: string;
    /** "N matches" text; only shown while a search term is entered. */
    matchText?: string;
}>();

const searchTerm = defineModel<string>("searchTerm", { required: true });

const emit = defineEmits<{
    next: [];
    clear: [];
}>();
</script>

<template>
    <div class="manual-header-root">
        <div class="manual-header">
            <strong class="manual-header-title">{{ heading }}</strong>
            <div class="manual-header-tools">
                <span v-if="searchTerm" class="search-match-count" :id="`${idPrefix}_text_match-count`">
                    {{ matchText }}
                </span>
                <InputGroup class="manual-header-search">
                    <InputText
                        v-model="searchTerm"
                        type="search"
                        :placeholder="placeholder"
                        @keydown.enter="emit('next')"
                        :id="`${idPrefix}_input-text_search`"
                    />
                    <Button outlined icon="pi pi-times" @click="emit('clear')" :id="`${idPrefix}_btn_clear-search`"></Button>
                </InputGroup>
                <slot name="trailing" />
            </div>
        </div>
    </div>
</template>

<style scoped>
/*
 * Mirrors the manual's own grid in public/asciidoc.css (.manual): a 16rem contents rail,
 * a 2rem gap and a panel column of at most 62rem, centred as one block, switching to the
 * compact layout below a 1000px container. So the title sits above the contents rail and
 * the search (with the dialog's close button, if any) ends exactly at the right edge of
 * the white panels.
 * Keep these numbers in sync with asciidoc.css.
 */
.manual-header-root {
    container-type: inline-size;
    width: 100%;
}

.manual-header {
    display: grid;
    grid-template-columns: 16rem minmax(0, 62rem);
    justify-content: center;
    column-gap: 2rem;
    align-items: center;
}

.manual-header-title {
    font-size: large;
}

.manual-header-tools {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    min-width: 0;
    max-width: 62rem;
}

.manual-header-search {
    width: min(100%, 25rem);
}

.search-match-count {
    color: var(--p-text-muted-color);
    white-space: nowrap;
}

/* Compact manual: panels span the container (still at most 62rem), the rail moves on top */
@container (max-width: 1000px) {
    .manual-header {
        grid-template-columns: minmax(0, 1fr) auto;
        max-width: 62rem;
    }
}

/* Phones: title on its own row, search across the full width below it */
@container (max-width: 560px) {
    .manual-header {
        grid-template-columns: minmax(0, 1fr);
        row-gap: 0.5rem;
    }

    .manual-header-search {
        flex: 1;
        width: auto;
    }
}
</style>
